import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useUser } from '@clerk/clerk-react'
import { useThemeColors } from '../hooks/useThemeColors'
import {
  getInvestors,
  updateInvestor,
  createInvestor,
  getMailingList,
  sendInvestorUpdate,
  triggerCalcomInvestorFollowup,
  type Investor,
  type InvestorStage,
} from '../lib/api'

const ADMIN_EMAILS = ['matt@primateintelligence.ai', 'mehdi@primateintelligence.ai']

const STAGES: { key: InvestorStage; label: string }[] = [
  { key: 'researching', label: 'Researching' },
  { key: 'reached_out', label: 'Reached Out' },
  { key: 'meeting_booked', label: 'Meeting Booked' },
  { key: 'pitched', label: 'Pitched' },
  { key: 'diligence', label: 'Diligence' },
  { key: 'passed', label: 'Passed' },
  { key: 'committed', label: 'Committed' },
]

// Stage colors are semantic (pipeline status badges) — intentionally fixed across themes.
// 'researching' uses rgba so it adapts naturally to both themes.
const STAGE_COLORS: Record<string, { bg: string; color: string }> = {
  researching:    { bg: 'rgba(155,164,181,0.15)', color: '#9BA4B5' },
  reached_out:    { bg: '#2058F022', color: '#2058F0' },
  meeting_booked: { bg: '#0EA5E922', color: '#38BDF8' },
  pitched:        { bg: '#A78BFA22', color: '#A78BFA' },
  diligence:      { bg: '#FB923C22', color: '#FB923C' },
  passed:         { bg: '#EF444422', color: '#F87171' },
  committed:      { bg: '#22C55E22', color: '#4ADE80' },
}

const FUND_STAGES = ['pre-seed', 'seed', 'series-a', 'series-b', 'growth', 'multi-stage']
const CONTACT_PATHS = ['cold_email', 'warm_intro', 'linkedin', 'conference', 'referral', 'other']

// ─── Add Investor Modal ───────────────────────────────────────────────────────

interface AddInvestorModalProps {
  onClose: () => void
  onSuccess: (investor: Investor) => void
}

function AddInvestorModal({ onClose, onSuccess }: AddInvestorModalProps) {
  const [name, setName] = useState('')
  const [firm, setFirm] = useState('')
  const [fundStage, setFundStage] = useState('')
  const [checkSize, setCheckSize] = useState('')
  const [contactPath, setContactPath] = useState('')
  const [stage, setStage] = useState<InvestorStage>('researching')
  const [email, setEmail] = useState('')
  const [linkedin, setLinkedin] = useState('')
  const [notes, setNotes] = useState('')
  const [nextAction, setNextAction] = useState('')
  const [nextActionDue, setNextActionDue] = useState('')
  const [lastContacted, setLastContacted] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit() {
    if (!firm.trim()) { setError('Fund / Firm is required'); return }
    if (!name.trim()) { setError('Contact Name is required'); return }
    setSaving(true)
    setError(null)
    try {
      const investor = await createInvestor({
        name: name.trim(),
        firm: firm.trim(),
        fund_stage: fundStage || null,
        check_size: checkSize || null,
        contact_path: contactPath || null,
        stage,
        email: email || null,
        linkedin: linkedin || null,
        notes: notes || null,
        next_action: nextAction || null,
        next_action_due: nextActionDue || null,
        last_contacted: lastContacted || null,
      })
      onSuccess(investor)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to create investor')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-obsidian border-l border-carbon w-full max-w-md h-full overflow-y-auto shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-carbon">
          <h2 className="text-heading-sm text-chalk">Add Investor</h2>
          <button onClick={onClose} className="text-steel hover:text-chalk text-title-1">✕</button>
        </div>
        <div className="flex-1 px-6 py-4 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded px-3 py-2 text-red-400 text-body-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-body-sm text-steel mb-1">
              Fund / Firm <span className="text-red-400">*</span>{' '}
              <span className="text-steel font-normal">(enter individual name if solo angel)</span>
            </label>
            <input
              value={firm}
              onChange={e => setFirm(e.target.value)}
              placeholder="Horizon Ventures"
              className="w-full bg-carbon border border-carbon rounded px-3 py-2 text-chalk text-body-sm focus:outline-none focus:border-signal"
            />
          </div>

          <div>
            <label className="block text-body-sm text-steel mb-1">Contact Name <span className="text-red-400">*</span></label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Sarah Chen"
              className="w-full bg-carbon border border-carbon rounded px-3 py-2 text-chalk text-body-sm focus:outline-none focus:border-signal"
            />
          </div>

          <div>
            <label className="block text-body-sm text-steel mb-1">Fund Stage</label>
            <select
              value={fundStage}
              onChange={e => setFundStage(e.target.value)}
              className="w-full bg-carbon border border-carbon rounded px-3 py-2 text-chalk text-body-sm focus:outline-none focus:border-signal"
            >
              <option value="">Select...</option>
              {FUND_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-body-sm text-steel mb-1">Check Size</label>
            <input
              value={checkSize}
              onChange={e => setCheckSize(e.target.value)}
              placeholder="$500k–$1M"
              className="w-full bg-carbon border border-carbon rounded px-3 py-2 text-chalk text-body-sm focus:outline-none focus:border-signal"
            />
          </div>

          <div>
            <label className="block text-body-sm text-steel mb-1">Contact Path</label>
            <select
              value={contactPath}
              onChange={e => setContactPath(e.target.value)}
              className="w-full bg-carbon border border-carbon rounded px-3 py-2 text-chalk text-body-sm focus:outline-none focus:border-signal"
            >
              <option value="">Select...</option>
              {CONTACT_PATHS.map(p => <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-body-sm text-steel mb-1">Stage</label>
            <select
              value={stage}
              onChange={e => setStage(e.target.value as InvestorStage)}
              className="w-full bg-carbon border border-carbon rounded px-3 py-2 text-chalk text-body-sm focus:outline-none focus:border-signal"
            >
              {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-body-sm text-steel mb-1">Email</label>
            <input
              value={email}
              onChange={e => setEmail(e.target.value)}
              type="email"
              placeholder="sarah@horizonvc.com"
              className="w-full bg-carbon border border-carbon rounded px-3 py-2 text-chalk text-body-sm focus:outline-none focus:border-signal"
            />
          </div>

          <div>
            <label className="block text-body-sm text-steel mb-1">LinkedIn</label>
            <input
              value={linkedin}
              onChange={e => setLinkedin(e.target.value)}
              placeholder="https://linkedin.com/in/..."
              className="w-full bg-carbon border border-carbon rounded px-3 py-2 text-chalk text-body-sm focus:outline-none focus:border-signal"
            />
          </div>

          <div>
            <label className="block text-body-sm text-steel mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              placeholder="Focus areas, background..."
              className="w-full bg-carbon border border-carbon rounded px-3 py-2 text-chalk text-body-sm focus:outline-none focus:border-signal resize-none"
            />
          </div>

          <div>
            <label className="block text-body-sm text-steel mb-1">Next Action</label>
            <input
              value={nextAction}
              onChange={e => setNextAction(e.target.value)}
              placeholder="Send deck"
              className="w-full bg-carbon border border-carbon rounded px-3 py-2 text-chalk text-body-sm focus:outline-none focus:border-signal"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-body-sm text-steel mb-1">Next Action Due</label>
              <input
                type="date"
                value={nextActionDue}
                onChange={e => setNextActionDue(e.target.value)}
                className="w-full bg-carbon border border-carbon rounded px-3 py-2 text-chalk text-body-sm focus:outline-none focus:border-signal"
              />
            </div>
            <div>
              <label className="block text-body-sm text-steel mb-1">Last Contacted</label>
              <input
                type="date"
                value={lastContacted}
                onChange={e => setLastContacted(e.target.value)}
                className="w-full bg-carbon border border-carbon rounded px-3 py-2 text-chalk text-body-sm focus:outline-none focus:border-signal"
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-carbon flex gap-3">
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 bg-signal text-white px-4 py-2 rounded text-body-sm font-medium hover:bg-signal/90 disabled:opacity-50"
          >
            {saving ? 'Adding...' : 'Add Investor'}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-steel hover:text-chalk text-body-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Investor Card ────────────────────────────────────────────────────────────

function InvestorCard({ investor, onClick, onToggleUpdates, onBookingMsg }: {
  investor: Investor
  onClick: () => void
  onToggleUpdates: (investor: Investor) => void
  onBookingMsg: (msg: string) => void
}) {
  const c = useThemeColors()
  const cardTitle = investor.firm === 'Personal' ? investor.name : investor.firm
  const cardSubtitle = investor.contacts?.[0]?.name ?? (investor.firm !== 'Personal' && investor.name !== investor.firm ? investor.name : null)
  const extraContacts = (investor.contacts?.length ?? 0) > 1 ? investor.contacts.length - 1 : 0
  const [calcomSending, setCalcomSending] = useState(false)
  const [calcomMsg, setCalcomMsg] = useState<{ type: 'success' | 'warning' | 'error'; text: string } | null>(null)

  async function handleSendBookingLink(e: React.MouseEvent) {
    e.stopPropagation()
    setCalcomSending(true)
    setCalcomMsg(null)
    try {
      const result = await triggerCalcomInvestorFollowup(investor.id)
      onBookingMsg(`✓ Booking link sent to ${result.to}`)
    } catch (err) {
      const error = err as { status?: number; message?: string }
      if (error.status === 409) {
        setCalcomMsg({ type: 'warning', text: 'Already booked' })
      } else {
        setCalcomMsg({ type: 'error', text: `Failed — ${error.message ?? 'error'}` })
      }
      setTimeout(() => setCalcomMsg(null), 4000)
    } finally {
      setCalcomSending(false)
    }
  }

  return (
    <div
      onClick={onClick}
      className="bg-obsidian border border-carbon rounded p-3 cursor-pointer hover:border-signal/40 transition-colors space-y-2"
    >
      <div>
        <div className="text-body-sm font-semibold mb-0.5" style={{ color: c.textPrimary }}>
          {cardTitle}
        </div>
        {cardSubtitle && (
          <div className="text-footnote mb-1 flex items-center gap-1" style={{ color: c.textMuted }}>
            <span>{cardSubtitle}</span>
            {extraContacts > 0 && <span style={{ opacity: 0.6 }}>+{extraContacts}</span>}
          </div>
        )}
      </div>
      {investor.is_current_investor && investor.amount_invested && (
        <div className="text-footnote px-2 py-0.5 rounded inline-block"
             style={{ background: '#22C55E22', color: '#4ADE80' }}>
          💰 ${(investor.amount_invested / 1000).toFixed(0)}K
        </div>
      )}
      {investor.check_size && (
        <span className="inline-block bg-aurora/20 text-aurora text-footnote px-2 py-0.5 rounded font-medium">
          {investor.check_size}
        </span>
      )}
      {investor.next_action && (
        <p className="text-steel text-footnote truncate">{investor.next_action}</p>
      )}
      <div className="flex items-center justify-between gap-2">
        <label onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={investor.receives_updates ?? false}
            onChange={() => onToggleUpdates(investor)}
          />
          <span style={{ color: c.textMuted, fontSize: '11px' }}>Updates</span>
        </label>
        {investor.email && (
          <button
            onClick={e => void handleSendBookingLink(e)}
            disabled={calcomSending}
            aria-label="Send booking link"
            title="📅 Send booking link"
            className="text-footnote px-2 py-0.5 bg-carbon text-chalk hover:bg-carbon/80 rounded transition-colors disabled:opacity-50"
          >
            {calcomSending ? '…' : '📅'}
          </button>
        )}
      </div>
      {calcomMsg && (
        <p className={`text-footnote ${
          calcomMsg.type === 'warning' ? 'text-yellow-400' : 'text-red-400'
        }`}>
          {calcomMsg.text}
        </p>
      )}
    </div>
  )
}

// ─── Send Update Modal ────────────────────────────────────────────────────────

function SendUpdateModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const c = useThemeColors()
  const now = new Date()
  const defaultSubject = `Primate Intelligence — ${now.toLocaleString('default', { month: 'long' })} ${now.getFullYear()} Update`

  const [subject, setSubject] = useState(defaultSubject)
  const [body, setBody] = useState('')
  const [recipientCount, setRecipientCount] = useState<number | null>(null)
  const [sending, setSending] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    getMailingList()
      .then(r => setRecipientCount(r.total))
      .catch(() => setRecipientCount(null))
  }, [])

  async function handleQueue() {
    if (!subject.trim() || !body.trim()) {
      setError('Subject and body are required')
      return
    }
    setSending(true)
    setError(null)
    try {
      await sendInvestorUpdate(subject, body)
      setToast('Draft queued')
      setTimeout(() => {
        onClose()
        navigate('/dashboard/admin/emails')
      }, 1500)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to queue draft')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-obsidian border-l border-carbon w-full max-w-lg h-full overflow-y-auto shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-carbon">
          <h2 className="text-heading-sm text-chalk font-semibold">📧 Send Investor Update</h2>
          <button onClick={onClose} className="text-steel hover:text-chalk text-title-1">✕</button>
        </div>

        <div className="flex-1 px-6 py-4 space-y-4">
          {toast && (
            <div className="bg-green-500/10 border border-green-500/30 rounded px-3 py-2 text-green-400 text-body-sm">
              ✓ {toast} →{' '}
              <button onClick={() => navigate('/dashboard/admin/emails')} className="text-signal hover:underline">
                View Email Queue
              </button>
            </div>
          )}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded px-3 py-2 text-red-400 text-body-sm">
              {error}
            </div>
          )}

          <div className="bg-carbon/40 rounded px-3 py-2 text-footnote" style={{ color: c.textMuted }}>
            Will be sent to{' '}
            <strong style={{ color: '#4ADE80' }}>
              {recipientCount !== null ? recipientCount : '…'} investors
            </strong>{' '}
            (BCC) + CC: mehdi@primateintelligence.ai
          </div>

          <div>
            <label className="block text-body-sm text-steel mb-1">Subject</label>
            <input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              className="w-full bg-carbon border border-carbon rounded px-3 py-2 text-chalk text-body-sm focus:outline-none focus:border-signal"
            />
          </div>

          <div>
            <label className="block text-body-sm text-steel mb-1">Body</label>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={12}
              placeholder="Write your investor update here..."
              className="w-full bg-carbon border border-carbon rounded px-3 py-2 text-chalk text-body-sm focus:outline-none focus:border-signal resize-none"
              style={{ minHeight: '300px' }}
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-carbon flex gap-3">
          <button
            onClick={handleQueue}
            disabled={sending}
            className="flex-1 text-white px-4 py-2 rounded text-body-sm font-medium disabled:opacity-50"
            style={{ background: c.signal }}
          >
            {sending ? 'Queuing...' : 'Queue Draft'}
          </button>
          <button onClick={onClose} className="px-4 py-2 text-steel hover:text-chalk text-body-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AdminInvestors() {
  const navigate = useNavigate()
  const { user } = useUser()
  const c = useThemeColors()
  const email = user?.primaryEmailAddress?.emailAddress ?? user?.emailAddresses?.[0]?.emailAddress ?? ''
  const isAdmin = ADMIN_EMAILS.includes(email)

  const [investors, setInvestors] = useState<Investor[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showSendUpdateModal, setShowSendUpdateModal] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  useEffect(() => {
    if (!isAdmin) return
    setLoading(true)
    getInvestors()
      .then(setInvestors)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [isAdmin])

  async function handleToggleUpdates(investor: Investor) {
    try {
      const updated = await updateInvestor(investor.id, { receives_updates: !investor.receives_updates })
      setInvestors(prev => prev.map(i => i.id === investor.id ? { ...i, ...updated } : i))
    } catch (e) {
      console.error('Failed to toggle updates:', e)
    }
  }

  async function handleExportBcc() {
    try {
      const result = await getMailingList()
      await navigator.clipboard.writeText(result.bcc_list)
      const count = result.bcc_list ? result.bcc_list.split(',').length : 0
      setToast(`✓ Copied ${count} emails to clipboard`)
      setTimeout(() => setToast(null), 3000)
    } catch (e) {
      console.error('Failed to export BCC:', e)
      setToast('Failed to copy emails')
      setTimeout(() => setToast(null), 3000)
    }
  }

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <p className="text-steel">Admin access required.</p>
      </div>
    )
  }

  const byStage = (stage: InvestorStage) => investors.filter(i => i.stage === stage)

  return (
    <div className="flex flex-col h-full bg-void">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-obsidian border border-carbon rounded px-4 py-2 text-body-sm text-chalk shadow-lg">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-carbon shrink-0">
        <div>
          <h1 className="text-heading-md text-chalk font-semibold">💰 Fundraise Tracker</h1>
          <p className="text-body-sm text-steel mt-0.5">{investors.length} total</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportBcc}
            className="bg-carbon text-steel px-3 py-2 rounded text-body-sm font-medium hover:text-chalk border border-carbon hover:border-steel transition-colors"
          >
            Export BCC
          </button>
          <button
            onClick={() => setShowSendUpdateModal(true)}
            className="text-white px-3 py-2 rounded text-body-sm font-medium hover:opacity-90"
            style={{ background: c.signal }}
          >
            📧 Send Update
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-signal text-white px-4 py-2 rounded text-body-sm font-medium hover:bg-signal/90"
          >
            + Add Investor
          </button>
        </div>
      </div>

      {/* Kanban board */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-steel">Loading...</p>
        </div>
      ) : (
        <div className="flex-1 overflow-x-auto">
          <div className="flex gap-4 p-4 h-full min-w-max">
            {STAGES.map(({ key, label }) => {
              const cards = byStage(key)
              const colors = STAGE_COLORS[key]
              return (
                <div key={key} className="w-56 shrink-0 flex flex-col">
                  {/* Column header */}
                  <div className="flex items-center justify-between mb-2 px-1">
                    <span
                      className="text-footnote font-semibold px-2 py-1 rounded"
                      style={{ background: colors.bg, color: colors.color }}
                    >
                      {label}
                    </span>
                    <span className="text-footnote text-steel">{cards.length}</span>
                  </div>
                  {/* Cards */}
                  <div className="flex-1 space-y-2 overflow-y-auto">
                    {cards.map(investor => (
                      <InvestorCard
                        key={investor.id}
                        investor={investor}
                        onClick={() => navigate(`/dashboard/investors/${investor.id}`)}
                        onToggleUpdates={handleToggleUpdates}
                        onBookingMsg={msg => {
                          setToast(msg)
                          setTimeout(() => setToast(null), 4000)
                        }}
                      />
                    ))}
                    {cards.length === 0 && (
                      <div className="border border-dashed border-carbon rounded p-3 text-center">
                        <p className="text-steel text-footnote">No investors</p>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {showAddModal && (
        <AddInvestorModal
          onClose={() => setShowAddModal(false)}
          onSuccess={(investor) => {
            setInvestors(prev => [investor, ...prev])
            setShowAddModal(false)
          }}
        />
      )}

      {showSendUpdateModal && (
        <SendUpdateModal onClose={() => setShowSendUpdateModal(false)} />
      )}
    </div>
  )
}
